import { useRef } from "react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import '../../styles/QuoteStyles/QuoteInfo.css';
import upload from '../../assets/images/icons/upload_100dp_343A40_FILL0_wght400_GRAD0_opsz48.png';
import { db, auth } from '../../firebase/config';
import { collection, addDoc } from 'firebase/firestore';
import { uploadFile } from '../../services/uploadService';
import SuccessOverlay from "./Overlay/Successoverlay";
import { licences } from "../../data/licences";
import { useCooldown } from "../../hooks/useCooldown";


const MAX_FILES = 8;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

// Light abuse protection (SECURITY.md, F-06). These only slow down a person or a simple bot in a
// browser: the form writes to Firebase directly, so a real rate limit needs a server step.
const COOLDOWN_SECONDS = 60;
// Anything sent faster than this after the page opened is almost certainly a bot (as on webinar registration)
const MIN_FILL_MS = 1500;
const COOLDOWN_KEY = "km-quote-sent-at";

const lastSentAt = () => {
  try { return Number(localStorage.getItem(COOLDOWN_KEY)) || 0; } catch { return 0; }
};

export default function QuoteInfo () {

    // Quotes are open to everyone — signed-in users just get their uid attached
    const user = auth.currentUser;
    const [searchParams] = useSearchParams();
    // "Request a Quote" on a licence card links here with ?licence=<id> to pre-select it
    const preselected = licences.find(l => l.id === searchParams.get("licence"))?.quoteValue ?? "";
    const [files, setFiles] = useState<File[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [submitError, setSubmitError] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);//change this to false initially, will be set to true on successful submission
  // Honeypot: hidden from people, so only bots fill it in
  const [website, setWebsite] = useState("");
  // After a request, the button waits a minute (kept across reloads in this browser)
  const cooldown = useCooldown(lastSentAt() + COOLDOWN_SECONDS * 1000);
  const openedAt = useRef(Date.now());
  const fileInputRef = useRef<HTMLInputElement>(null);

// Build the WhatsApp message and redirect
const whatsAppMessage = () => {
    const message = `
    *KOLE MEDICAL — QUOTE REQUEST*

    *Full Name:* ${form.fullName}
    *Phone Number:* ${form.phoneNum}
    *Email:* ${form.email}
    *Company Name:* ${form.compName}

    *Business Type:* ${form.licenseTypes.length > 0 ? form.licenseTypes.join(", ") : "Not specified"}
    *License Required:* ${form.licenseType}

    *Project Details:*
    ${form.projectDetails}

    *Documents:* ${files.length > 0 ? files.map(f => f.name).join(", ") : "No documents attached"}
        `.trim();

    // Your WhatsApp number — include country code, no + or spaces
    const phoneNumber = "27645406751";

    // Encode the message for a URL
    const encodedMessage = encodeURIComponent(message);

    // Build the WhatsApp URL
    const whatsAppURL = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

    // Open WhatsApp in a new tab
    return window.open(whatsAppURL, "_blank");
};


    // Handle file selection
const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    if (newFiles.length === 0) return;
 
    setError("");

      // Check individual file sizes
    const oversizedFiles = newFiles.filter(file => file.size > MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setError(`File too large: ${oversizedFiles.map(f => f.name).join(", ")}. Max size is 10MB per file.`);
      e.target.value = ""; // reset input so the same file can be re-selected
      return;
    }

    // Check total file count
    const combined = [...files, ...newFiles];
    if (combined.length > MAX_FILES) {
      setError(`You can only upload up to ${MAX_FILES} files. You tried to add ${newFiles.length}, but already have ${files.length}.`);
      e.target.value = "";
      return;
    }

     setFiles(combined);
    e.target.value = ""; // allow re-selecting the same file later
};

const handleAddClick = () => {
    fileInputRef.current?.click();
  };

   const handleClearAll = () => {
    setFiles([]);
    setError("");
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setError("");
  };

// Form state
  const [form, setForm] = useState({
    fullName: "",
    phoneNum: "",
    email: "",
    compName: "",
    licenseTypes: [] as string[],
    licenseType: preselected,
    projectDetails: "",
  });

  // Handle text inputs
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Handle checkboxes
  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setForm(prev => ({
      ...prev,
      licenseTypes: checked
        ? [...prev.licenseTypes, value]          // add if checked
        : prev.licenseTypes.filter(t => t !== value) // remove if unchecked
    }));
  };

// Build the WhatsApp message and redirect
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault(); // stop default form behaviour

    // Optionally, you can also save the quote request to Firestore
    saveQuoteRequest(e);
  };

const saveQuoteRequest = async (
  e: React.FormEvent<HTMLFormElement>
) => {

  e.preventDefault();
  if (loading || cooldown.active) return;

  const clearForm = () => {
    setForm({
      fullName: "",
      phoneNum: "",
      email: "",
      compName: "",
      licenseTypes: [],
      licenseType: "",
      projectDetails: "",
    });
    setFiles([]);
  };

  // A filled honeypot means a bot: look successful, but save and send nothing
  if (website.trim() !== "") {
    setShowSuccess(true);
    clearForm();
    return;
  }

  // Too fast to be a person: refuse, with the same plain message as any failure
  if (Date.now() - openedAt.current < MIN_FILL_MS) {
    setSubmitError("We couldn't send your request. Please try again, or contact us on WhatsApp at +27 64 540 6751.");
    return;
  }

  setLoading(true); // ← start loading
  setSubmitError("");

  try {

    // Uploads sit inside the try so a failed upload can't leave the button stuck on "Submitting..."
    const documentFiles: { name: string; url: string }[] = [];
    for (const file of files) {
      const url = await uploadFile(file, user ? user.uid : "guest");
      documentFiles.push({ name: file.name, url });
    }

    await addDoc(collection(db, "quotes"), {

      // Signed-in user ID, or null for guests
      userId: user ? user.uid : null,

      // Form data
      fullName: form.fullName || "Not provided",
      phoneNum: form.phoneNum || "Not provided",
      email: form.email || "Not provided",
      compName: form.compName || "Not provided",
      licenseTypes: form.licenseTypes || [],
      licenseType: form.licenseType || "Not provided",
      projectDetails: form.projectDetails || "No details provided",
      documentFiles: documentFiles || [],

      // Metadata
      createdAt: new Date(),
      status: "pending"

    });

    whatsAppMessage();
    setShowSuccess(true);

    const sentAt = Date.now();
    try { localStorage.setItem(COOLDOWN_KEY, String(sentAt)); } catch { /* storage blocked: the cooldown lasts until reload */ }
    cooldown.start(sentAt + COOLDOWN_SECONDS * 1000);

    // ← clear the form after success
    clearForm();

  } catch (error) {

    console.error(error);
    setSubmitError("We couldn't send your request. Please try again, or contact us on WhatsApp at +27 64 540 6751.");

  }
  finally{
    setLoading(false); // ← stop loading
  }
};

    return(
        <section className="quoteInfoContainer">
            
            {/* Show overlay when form is submitted */}
            {showSuccess && (
            <SuccessOverlay onClose={() => setShowSuccess(false)} />
            )}

            <form onSubmit={handleSubmit} className="formPage">
                {/* Honeypot: off-screen, so only bots fill it in */}
                <div className="qt-honeypot" aria-hidden="true">
                    <label htmlFor="quote-website">Website</label>
                    <input id="quote-website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={e => setWebsite(e.target.value)} />
                </div>
                <div className="left-content">
                    <h4>Personal Information</h4>
                    <div>
                        <div className="form-group">
                            <label htmlFor="fullName">Full Name</label>
                            {/* Full Name */}
                            <input
                                type="text"
                                name="fullName"
                                id="fullName"
                                required
                                value={form.fullName}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="phoneNum">Phone Number</label>
                            {/* Phone Number */}
                            <input
                                type="tel"
                                name="phoneNum"
                                id="phoneNum"
                                value={form.phoneNum}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">Email Address</label>
                            {/* Email */}
                            <input
                                type="email"
                                name="email"
                                id="email"
                                required
                                value={form.email}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="compName">Company Name</label>
                            {/* Company Name */}
                            <input
                                type="text"
                                name="compName"
                                id="compName"
                                required
                                value={form.compName}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>
                <hr/>
                <div className="right-content">
                    <h4>Business Information</h4>
                    <div>
                        <div className="form-checker">
                            <p>Business Type</p>
                            <label className="checkbox-item">
                                <input
                                    type="checkbox"
                                    name="license-type"
                                    value="Distributor"
                                    required={form.licenseTypes.length === 0} // require at least one
                                    onChange={handleCheckbox}
                                    />
                                Distributor
                            </label>

                            <label className="checkbox-item">
                                <input
                                    type="checkbox"
                                    name="license-type"
                                    value="Wholesaler"
                                    required={form.licenseTypes.length === 0}
                                    onChange={handleCheckbox}
                                    />
                                Wholesaler
                            </label>

                            <label className="checkbox-item">
                                <input
                                    type="checkbox"
                                    name="license-type"
                                    value="Manufacturer"
                                    required={form.licenseTypes.length === 0}
                                    onChange={handleCheckbox}
                                    />
                                Manufacturer
                            </label>
                        </div>

                        <div className="form-dropdown">
                            <label htmlFor="licenseType">Select Licence Needed</label>
                            {/* License Type Select */}
                            <select
                                name="licenseType"
                                id="licenseType"
                                value={form.licenseType}
                                required
                                onChange={handleChange}
                            >
                                <option value="">Select a licence</option>
                                {licences.map((licence) => (
                                    <option key={licence.id} value={licence.quoteValue}>{licence.title}</option>
                                ))}
                            </select>
                        </div>
                         <div className="form-file-upload">
                            <label htmlFor="uploadedDocument" className="upload-box">
                                <img src={upload} alt="Upload Icon" />
                                <div className="file-list">
                                {files.length > 0 ? (
                                    files.map((file, index) => (
                                    <div className="file-item" key={index}>
                                        <span className="file-name">{file.name}</span>
                                        <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                        <button
                                        type="button"
                                        className="file-remove-btn"
                                        onClick={e => {
                                            e.preventDefault(); // prevent triggering the label's file picker
                                            handleRemoveFile(index);
                                        }}
                                        aria-label={`Remove ${file.name}`}
                                        >
                                        ✕
                                        </button>
                                    </div>
                                    ))
                                ) : (
                                    <p>No files selected</p>
                                )}
                                </div>
                            </label>
                        
                            <input
                                type="file"
                                id="uploadedDocument"
                                name="uploadedDocument"
                                ref={fileInputRef}
                                multiple
                                accept=".pdf,.doc,.docx"
                                hidden
                                onChange={handleFileChange}
                            />
                        
                            <div className="upload-meta">
                                <span className="file-count">{files.length} / {MAX_FILES} files</span>
                                <span className="file-hint">PDF or Word, max 10MB each</span>
                            </div>
                        
                            {error && <p className="upload-error">{error}</p>}
                        
                            <div className="upload-actions">
                                <button type="button" className="btn-add-file" onClick={handleAddClick} disabled={files.length >= MAX_FILES}>
                                + Add Files
                                </button>
                                <button type="button" className="btn-clear-files" onClick={handleClearAll} disabled={files.length === 0}>
                                Clear All
                                </button>
                            </div>
                            </div>
                        <div className="form-group">
                            <label htmlFor="projectDetails">Project Details</label>
                            <textarea
                                name="projectDetails"
                                id="projectDetails"
                                placeholder="Describe your needs..."
                                value={form.projectDetails}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Full-width footer: submit comes after every field, on every screen size */}
                <div className="submitSection">
                    <button
                        type="submit"
                        disabled={loading || cooldown.active} /* Disable while sending, and for a minute after */>
                            {/* Show different text depending on state */}
                                {loading ? (
                                    <>
                                    <span className="btn-spinner" />
                                    Submitting...
                                    </>
                                ) : cooldown.active ? (
                                    `Request sent · another in ${cooldown.label}`
                                ) : (
                                    "Request Quote"
                                )}
                    </button>
                    {submitError && <p className="submit-error" role="alert">{submitError}</p>}
                    <p>We will contact you by email or phone within 1–2 business days.</p>
                </div>
            </form>
        </section>
    );
};